import React from 'react';
import { Checkbox, Select } from '../ui/Field';
import { CASES_SOBRES, type PlageChoisie, type ModePlage } from './fermetures';

/**
 * Choix d'une plage horaire pour une fermeture : « tout le service », « à partir de 21:00 », « entre deux heures » ou « certains créneaux »
 * (cases à cocher). L'option « à partir de » est la plus rapide quand on veut fermer la fin de service : une seule heure à choisir.
 */
interface Props {
    creneaux: string[];
    /** Heure de fermeture du service : fin de la plage « à partir de » et dernière borne de « entre ». */
    finService: string;
    value: PlageChoisie;
    onChange: (v: PlageChoisie) => void;
    /** « Tout le service » n'a de sens que pour les tailles de groupe (pour les créneaux, c'est l'option « Tout le service » de la fermeture). */
    avecService?: boolean;
    nom: string;
}

const LIBELLES: Record<ModePlage, string> = { service: 'Tout le service', apartir: 'À partir de', entre: 'Entre deux heures', cases: 'Certains créneaux' };

export const SelecteurPlage: React.FC<Props> = ({ creneaux, finService, value, onChange, avecService = false, nom }) => {
    const modes: ModePlage[] = [...(avecService ? ['service' as const] : []), 'apartir', 'entre', 'cases'];
    const maj = (p: Partial<PlageChoisie>) => onChange({ ...value, ...p });
    const bornesFin = [...creneaux.filter((h) => h > value.de), finService];
    return (
        <div className="space-y-2.5">
            <div role="group" aria-label={`Plage horaire : ${nom}`} className="flex flex-wrap gap-2">
                {modes.map((m) => (
                    <button key={m} type="button" aria-pressed={value.mode === m} onClick={() => maj({ mode: m })}
                        className={`h-9 max-lg:h-11 px-3.5 rounded-full border text-[12.5px] font-semibold ${value.mode === m ? 'bg-content text-surface border-transparent' : 'bg-surface text-content border-border hover:border-content'}`}>{LIBELLES[m]}</button>
                ))}
            </div>
            {value.mode === 'apartir' && (
                <label className="flex flex-wrap items-center gap-2 text-[13px] text-content">À partir de
                    <Select aria-label={`Fermer à partir de (${nom})`} className="!w-[110px] max-lg:!h-11" value={value.de} onChange={(e) => maj({ de: e.target.value })}>{creneaux.map((h) => <option key={h} value={h}>{h}</option>)}</Select>
                    <span className="text-content-muted">jusqu'à la fin du service ({finService})</span>
                </label>
            )}
            {value.mode === 'entre' && (
                <div className="flex flex-wrap items-center gap-2 text-[13px] text-content">
                    <label className="flex items-center gap-2">De
                        <Select aria-label={`Début de la plage (${nom})`} className="!w-[110px] max-lg:!h-11" value={value.de} onChange={(e) => maj({ de: e.target.value, a: e.target.value >= value.a ? '' : value.a })}>{creneaux.map((h) => <option key={h} value={h}>{h}</option>)}</Select></label>
                    <label className="flex items-center gap-2">jusqu'à
                        <Select aria-label={`Fin de la plage (${nom})`} className="!w-[110px] max-lg:!h-11" value={bornesFin.includes(value.a) ? value.a : bornesFin[0]} onChange={(e) => maj({ a: e.target.value })}>{bornesFin.map((h) => <option key={h} value={h}>{h}</option>)}</Select></label>
                </div>
            )}
            {value.mode === 'cases' && (
                <div role="group" aria-label={`Créneaux : ${nom}`} className={`${CASES_SOBRES} grid grid-cols-4 max-sm:grid-cols-3 gap-2`}>
                    {creneaux.map((h) => <Checkbox key={h} label={h} checked={value.cases.includes(h)} onChange={() => maj({ cases: value.cases.includes(h) ? value.cases.filter((x) => x !== h) : [...value.cases, h] })} className="max-lg:min-h-[44px]" />)}
                </div>
            )}
        </div>
    );
};
